// src/pages/Admin/Adherents/AdherentPalmares.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getPalmaresByAdherentId } from "../../../services/palmaresServices";

export default function AdherentPalmares({ adherentId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!adherentId) return;
    (async () => {
      try {
        setLoading(true);
        const data = await getPalmaresByAdherentId(adherentId);
        setItems(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Erreur lors de la récupération des palmarès:", e);
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [adherentId]);

  const rows = useMemo(() => {
    return [...items].sort(
      (a, b) =>
        new Date(b?.date_palmares || 0).getTime() -
        new Date(a?.date_palmares || 0).getTime()
    );
  }, [items]);

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("fr-FR", {
          weekday: "short",
          day: "2-digit",
          month: "long",
          year: "numeric",
        })
      : "—";

  const splitResults = (str = "") =>
    str
      .split("|")
      .map((s) => s.trim())
      .filter(Boolean);

  const badge = (text, classes) => (
    <span
      className={`inline-flex items-center rounded-md px-2.5 py-1 text-[11px] font-semibold ring-1 ${classes}`}
    >
      {text}
    </span>
  );

  const classementBadgeClasses = (classement = "") => {
    const c = String(classement).toLowerCase();
    if (/(^1$|1er|1ère)/.test(c))
      return "bg-yellow-100 text-yellow-900 ring-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-200 dark:ring-yellow-800/50";
    if (/(^2$|2e|2ème)/.test(c))
      return "bg-slate-200 text-slate-800 ring-slate-300 dark:bg-slate-700/40 dark:text-slate-100 dark:ring-slate-600/50";
    if (/(^3$|3e|3ème)/.test(c))
      return "bg-orange-100 text-orange-900 ring-orange-200 dark:bg-orange-900/40 dark:text-orange-200 dark:ring-orange-800/50";
    return "bg-violet-100 text-violet-900 ring-violet-200 dark:bg-violet-900/40 dark:text-violet-200 dark:ring-violet-800/50";
  };

  const activeBadgeClasses = (isActive) =>
    isActive
      ? "bg-emerald-100 text-emerald-900 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:ring-emerald-800/50"
      : "bg-slate-100 text-slate-800 ring-slate-200 dark:bg-slate-900/40 dark:text-slate-100 dark:ring-slate-800/50";

  return (
    <section className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur">
      {/* En-tête harmonisé */}
      <div className="px-5 pt-5 sm:px-6">
        <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
          Palmarès
        </h3>
        <div className="mt-2 h-1 w-20 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
      </div>

      {/* Contenu */}
      <div className="px-5 pb-6 sm:px-6 sm:pb-6 mt-4">
        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="animate-pulse rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-5 bg-white/60 dark:bg-slate-900/40 h-24"
              />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-5 bg-slate-50 dark:bg-slate-900/40 text-sm text-slate-700 dark:text-slate-300">
            Aucun palmarès disponible.
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((p) => {
              const date = formatDate(p?.date_palmares);
              const titre = p?.titre_palmares || "Sans titre";
              const results = splitResults(p?.resultats_palmares);
              const classement = p?.classement_palmares;
              const obs = p?.observations_palmares;
              const isActive = !!p?.is_active;

              return (
                <div
                  key={`${p.id}-${p.date_palmares}-${p.titre_palmares}`}
                  className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-50 dark:bg-slate-900/40"
                >
                  {/* Ligne titre + date + badges principaux */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                    <div>
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        🏆 {titre}
                      </div>
                      <div className="text-xs text-slate-600 dark:text-slate-300">
                        {date}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {classement &&
                        badge(
                          `Classement : ${classement}`,
                          classementBadgeClasses(classement)
                        )}
                      {badge(isActive ? "Actif" : "Archivé", activeBadgeClasses(isActive))}
                    </div>
                  </div>

                  {/* Résultats */}
                  {results.length > 0 && (
                    <div className="mt-3">
                      <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                        Résultats
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {results.map((r, i) =>
                          badge(
                            r,
                            /✅/.test(r)
                              ? "bg-emerald-100 text-emerald-800 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:ring-emerald-800/50"
                              : /❌/.test(r)
                              ? "bg-rose-100 text-rose-900 ring-rose-200 dark:bg-rose-900/40 dark:text-rose-200 dark:ring-rose-800/50"
                              : "bg-slate-100 text-slate-800 ring-slate-200 dark:bg-slate-900/40 dark:text-slate-100 dark:ring-slate-800/50"
                          )
                        )}
                      </div>
                    </div>
                  )}

                  {/* Observations */}
                  {obs && (
                    <div className="mt-3 rounded-lg ring-1 ring-slate-200 dark:ring-slate-800 bg-white/80 dark:bg-slate-900/30 px-3 py-2 text-sm">
                      <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Observations
                      </div>
                      <div className="text-slate-800 dark:text-slate-100 mt-0.5">
                        {obs}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
